import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import StagingDisplayTable from './StagingDisplayTable';
import ColumnDefinition from '../constants/ColumnDefinition';
import { dateToISO8601 } from '../../../utils/parsing';
import { groupListByProperty } from '../../../utils/collections';
import FilterContext from '../contexts/FilterContext';

const DevicesDataTable = ({ buildingId, devices, deployments, points }) => {
  const { filters } = useContext(FilterContext);
  const filteredDevices = useMemo(
    () => Array.from(devices.values().filter((point) => filters.every((f) => f.filter(point)))),
    [devices, filters]
  );
  const pointsByDevice = groupListByProperty('staging_device_id', points.values());

  const columns = [
    new ColumnDefinition({
      id: 'device_id',
      label: 'Device ID',
      type: 'text',
      hideable: true,
      sortable: true,
      render: (device) => device.device_id,
    }),
    new ColumnDefinition({
      id: 'device_name',
      label: 'Device Name',
      type: 'text',
      sortable: true,
      hideable: true,
      render: (device) => device.name,
    }),
    new ColumnDefinition({
      id: 'deployment_site',
      label: 'Deployment Name',
      type: 'text',
      sortable: true,
      hideable: true,
      defaultHidden: true,
      render: (device) => deployments[device.deployment_site],
    }),
    new ColumnDefinition({
      id: 'device_protocol',
      label: 'Collection Protocol',
      type: 'text',
      sortable: true,
      hideable: true,
      defaultHidden: true,
      render: (device) => device.protocol,
    }),
    new ColumnDefinition({
      id: 'device_description',
      label: 'Description',
      type: 'text',
      hideable: true,
      render: (device) => device.properties?.description,
    }),
    new ColumnDefinition({
      id: 'device_point_count',
      label: 'Point Count',
      type: 'number',
      hideable: true,
      sortable: true,
      render: (device) => pointsByDevice[device.staging_id]?.length ?? 0,
    }),
    new ColumnDefinition({
      id: 'first_discovered_time',
      label: 'First Discovered Time',
      type: 'time',
      hideable: true,
      sortable: true,
      render: (device) => dateToISO8601(new Date(device.created)),
    }),
    new ColumnDefinition({
      id: 'last_discovered_time',
      label: 'Last Discovered Time',
      type: 'time',
      hideable: true,
      sortable: true,
      render: (device) => dateToISO8601(new Date(device.last_discovery)),
    }),
  ];

  return (
    <StagingDisplayTable
      title="Devices"
      rowIdKey="id"
      columns={columns}
      allData={devices}
      filteredData={filteredDevices}
      buildingId={buildingId}
    />
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  buildingId,
  units: state.entities.units.byId,
  pointTypes: state.entities.pointTypes.byId,
  deployments:
    Object.fromEntries(
      Object.values(state.entities.deployments.deployments)?.map((d) => [d.site, d.name])
    ) ?? {},
});

export default connect(mapStateToProps)(DevicesDataTable);
